/**
 * AC create + edit wizard. Client card (shared) + a dynamic list of
 * divisions (rooms) with a predefined-division dropdown ("Outra…" reveals a
 * free-text field), per-row live BTU preview, a running total + suggested
 * topology. Mirrors the engine heuristics so the comercial sees the sizing
 * before submit.
 *
 * Doubles as the edit page: when a `:id` route param is present it loads the
 * existing proposal, prefills the form, and submits via the generic
 * `actions/update` (re-runs the engine). Otherwise it creates a new one.
 */

import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ChevronRight, Loader2, Plus, Trash2, Wind } from "lucide-react"
import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { ClientLeadCard, type ClientFields } from "@/components/ClientLeadCard"
import { isLeadOriginComplete, leadOriginFromRow, leadOriginPayload } from "@/lib/lead-origin"
import { createAc, updateAc, getProposal, listAcProducts } from "@/lib/api"
import { AC_DEFAULT_W_PER_M2, useModuleWPerM2, W_TO_BTU } from "@/lib/module-sizing"

const STANDARD_BTU = [7000, 9000, 12000, 18000, 24000]
function snapBtu(needed: number): number {
  for (const s of STANDARD_BTU) if (s >= needed) return s
  return STANDARD_BTU[STANDARD_BTU.length - 1]
}

/** Divisões mais comuns numa habitação — o comercial escolhe da lista. */
const ROOM_PRESETS = [
  "Sala", "Sala de jantar", "Quarto", "Suite", "Cozinha",
  "Escritório", "Hall", "Casa de banho", "Open space", "Loja",
]
const OTHER = "__other__"

/**
 * `group` = sistema MultiSplit (1, 2, …) — divisões com o mesmo número
 * partilham uma unidade exterior (ex.: um por piso). Ignorado em mono.
 */
interface Division { name: string; area: string; custom: boolean; topology: "mono" | "multi"; group: number }

const EMPTY_CLIENT: ClientFields = {
  lead_id: null, odoo_lead_id: null, odoo_lead_name: null,
  lead_origin: null, lead_origin_detail: "",
  client_name: "", client_email: "", client_phone: "",
  address: "", city: "", postal_code: "",
}

function newDiv(
  name = "", area = "", custom = false, topology: "mono" | "multi" = "mono", group = 1,
): Division {
  return { name, area, custom, topology, group }
}

export function AcWizard({ shell }: { shell: AppShell | null }) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)

  const [client, setClient] = useState<ClientFields>(EMPTY_CLIENT)
  const [divisions, setDivisions] = useState<Division[]>([newDiv("Sala", "20")])
  // Marca AC — escolhida logo após os dados do cliente; filtra todo o catálogo
  // nas etapas seguintes (pedido do cliente 04-09).
  const [brand, setBrand] = useState<string>("")
  const [brands, setBrands] = useState<string[]>([])
  const [phase, setPhase] = useState<"mono" | "tri">("mono")
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(isEdit)

  // Edit mode: load the existing proposal and prefill.
  useEffect(() => {
    if (!shell || !id) return
    setLoading(true)
    getProposal(shell, id)
      .then((raw) => {
        const p = raw as Record<string, unknown>
        setClient({
          lead_id: (p.lead_id as number | string | null) ?? null,
          odoo_lead_id: Number(p.odoo_lead_id) > 0 ? Number(p.odoo_lead_id) : null,
          odoo_lead_name: (p.odoo_lead_name as string | null) ?? null,
          ...leadOriginFromRow(p),
          client_name: (p.client_name as string) ?? "",
          client_email: (p.client_email as string) ?? "",
          client_phone: (p.client_phone as string) ?? "",
          address: (p.address as string) ?? "",
          city: (p.city as string) ?? "",
          postal_code: (p.postal_code as string) ?? "",
        })
        const divs = Array.isArray(p.divisions_json) ? (p.divisions_json as Array<Record<string, unknown>>) : []
        if (divs.length) {
          setDivisions(divs.map((d) => {
            const name = typeof d.name === "string" ? d.name : ""
            const t = d.topology === "multi" ? "multi" : "mono"
            const g = Number(d.multi_group) >= 1 ? Number(d.multi_group) : 1
            return newDiv(name, String(d.area_m2 ?? ""), !ROOM_PRESETS.includes(name), t, g)
          }))
        }
        if (typeof p.brand === "string" && p.brand.trim()) setBrand(p.brand.trim())
      })
      .catch((e) => shell.bridge.toast({ variant: "error", description: e instanceof Error ? e.message : "Falha a carregar" }))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell, id])

  // Marcas disponíveis = marcas distintas do catálogo AC (app-owned).
  useEffect(() => {
    if (!shell) return
    let cancelled = false
    listAcProducts(shell)
      .then((items) => {
        if (cancelled) return
        const uniq = Array.from(
          new Set(items.map((i) => (i.brand ?? "").trim()).filter(Boolean)),
        ).sort()
        setBrands(uniq)
        setBrand((b) => b || uniq[0] || "")
      })
      .catch(() => {
        if (!cancelled) setBrands([])
      })
    return () => {
      cancelled = true
    }
  }, [shell])

  // W/m² das Configurações → BTU/m² (o motor faz a mesma conta).
  const wPerM2 = useModuleWPerM2(shell, "ac_config", AC_DEFAULT_W_PER_M2)
  const btuPerM2 = Math.round(wPerM2 * W_TO_BTU)
  const rows = useMemo(
    () => divisions.map((d) => {
      const area = Number(d.area) || 0
      const btu = area > 0 ? snapBtu(Math.ceil(area * btuPerM2)) : 0
      return { ...d, area, btu }
    }),
    [divisions, btuPerM2],
  )
  const validDivs = rows.filter((r) => r.area > 0)
  const totalBtu = validDivs.reduce((s, r) => s + r.btu, 0)
  const monoCount = validDivs.filter((r) => r.topology === "mono").length
  const multiCount = validDivs.length - monoCount
  const multiSystems = new Set(validDivs.filter((r) => r.topology === "multi").map((r) => r.group)).size
  const multiTxt = `${multiSystems > 1 ? `${multiSystems} ` : ""}MultiSplit (${multiCount})`
  const topoLabel =
    monoCount > 0 && multiCount > 0 ? `Mono (${monoCount}) + ${multiTxt}`
      : multiCount > 0 ? multiTxt : "Mono"
  // Opções de sistema MultiSplit: os já usados + mais um (até ao nº de divisões).
  const maxGroup = Math.max(0, ...divisions.filter((d) => d.topology === "multi").map((d) => d.group))
  const groupOptions = Array.from(
    { length: Math.max(1, Math.min(maxGroup + 1, divisions.length)) },
    (_, i) => i + 1,
  )

  const canSubmit = client.client_name.trim().length > 0 && isLeadOriginComplete(client) && validDivs.length > 0 && !submitting

  function setDiv(i: number, patch: Partial<Division>) {
    setDivisions((ds) => ds.map((d, idx) => (idx === i ? { ...d, ...patch } : d)))
  }
  const addDiv = () => setDivisions((ds) => [...ds, newDiv()])
  const removeDiv = (i: number) => setDivisions((ds) => ds.filter((_, idx) => idx !== i))

  /** Select changed: a preset name, or "Outra…" → switch the row to free text. */
  function onPickPreset(i: number, value: string) {
    if (value === OTHER) setDiv(i, { custom: true, name: "" })
    else setDiv(i, { custom: false, name: value })
  }

  async function submit() {
    if (!shell || !canSubmit) return
    setSubmitting(true)
    const payload = {
      lead_id: client.lead_id,
      odoo_lead_id: client.odoo_lead_id,
      odoo_lead_name: client.odoo_lead_name,
      ...leadOriginPayload(client),
      client_name: client.client_name.trim(),
      client_email: client.client_email.trim() || null,
      client_phone: client.client_phone.trim() || null,
      address: client.address.trim() || null,
      city: client.city.trim() || null,
      postal_code: client.postal_code.trim() || null,
      brand: brand || null,
      divisions: validDivs.map((r) => ({
        name: r.name.trim() || "Divisão",
        area_m2: r.area,
        topology: r.topology,
        multi_group: r.topology === "multi" ? r.group : undefined,
      })),
      phase,
    }
    try {
      const res = isEdit
        ? await updateAc(shell, id!, payload)
        : await createAc(shell, payload)
      shell.bridge.toast({ variant: "success", description: `Orçamento ${res.number} ${isEdit ? "guardado" : "criado"}` })
      navigate(isEdit ? `/ac/${id}` : "/list")
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha a guardar" })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">A carregar…</p>

  return (
    <div className="space-y-6">
      <div>
        <nav className="flex items-center gap-1 text-xs text-muted-foreground">
          <button onClick={() => navigate("/simulador")} className="hover:underline">Início</button>
          <ChevronRight className="h-3 w-3" />
          <span>{isEdit ? "Editar orçamento" : "Novo orçamento"}</span>
        </nav>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Wind className="h-6 w-6 text-accent" />
          Ar Condicionado
        </h1>
      </div>

      <ClientLeadCard shell={shell} module="ac" value={client} onChange={(p) => setClient((c) => ({ ...c, ...p }))} />

      {/* Marca AC — "Após os dados do Cliente: disponibilizar um campo para
          seleção da MARCA". A marca determina os equipamentos e combinações
          disponíveis nas etapas seguintes (filtro estrito no motor). */}
      <Card>
        <CardHeader>
          <CardTitle>Marca</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-xs space-y-1">
            <Label>Marca de AC</Label>
            <Select value={brand} onValueChange={setBrand} disabled={brands.length === 0}>
              <SelectTrigger>
                <SelectValue placeholder={brands.length ? "Escolhe a marca…" : "A carregar…"} />
              </SelectTrigger>
              <SelectContent>
                {brands.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Determina os equipamentos e combinações disponíveis nas divisões.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Divisões a climatizar
            <Button size="sm" variant="outline" onClick={addDiv}>
              <Plus className="h-4 w-4" /> Adicionar divisão
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <Label>Divisão</Label>
                <Select
                  value={r.custom ? OTHER : r.name}
                  onValueChange={(v) => onPickPreset(i, v)}
                >
                  <SelectTrigger><SelectValue placeholder="Escolhe a divisão…" /></SelectTrigger>
                  <SelectContent>
                    {ROOM_PRESETS.map((room) => (
                      <SelectItem key={room} value={room}>{room}</SelectItem>
                    ))}
                    <SelectItem value={OTHER}>Outra…</SelectItem>
                  </SelectContent>
                </Select>
                {r.custom && (
                  <Input
                    className="mt-1"
                    placeholder="Nome da divisão"
                    value={r.name}
                    onChange={(e) => setDiv(i, { name: e.target.value })}
                  />
                )}
              </div>
              <div className="w-28 space-y-1">
                <Label>Área (m²)</Label>
                <Input
                  type="number" min={1} step={1}
                  value={r.area === 0 ? "" : String(divisions[i].area)}
                  onChange={(e) => setDiv(i, { area: e.target.value })}
                />
              </div>
              <div className="w-24 space-y-1">
                <Label>BTU</Label>
                <div className="flex h-9 items-center text-sm font-medium text-accent">
                  {r.btu ? r.btu.toLocaleString("pt-PT") : "—"}
                </div>
              </div>
              <div className="w-40 space-y-1">
                <Label>Tipologia</Label>
                <Select
                  value={r.topology === "mono" ? "mono" : `multi:${r.group}`}
                  onValueChange={(v) =>
                    v === "mono"
                      ? setDiv(i, { topology: "mono" })
                      : setDiv(i, { topology: "multi", group: Number(v.split(":")[1]) || 1 })
                  }
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mono">MonoSplit</SelectItem>
                    {groupOptions.map((g) => (
                      <SelectItem key={g} value={`multi:${g}`}>MultiSplit {g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="icon" variant="ghost"
                onClick={() => removeDiv(i)}
                disabled={divisions.length <= 1}
                aria-label="Remover divisão"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <p className="text-xs text-muted-foreground">
            Divisões com o mesmo MultiSplit (1, 2, …) partilham uma unidade exterior — ex.: um
            MultiSplit por piso. Os acessórios de instalação contam 1 por máquina interior.
          </p>

          <div className="grid grid-cols-1 gap-3 border-t border-border pt-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Fase</Label>
              <Select value={phase} onValueChange={(v) => setPhase(v as typeof phase)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mono">Monofásica</SelectItem>
                  <SelectItem value="tri">Trifásica</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {validDivs.length > 0 && (
            <div className="rounded-md border border-accent/40 bg-accent/10 px-3 py-2 text-sm font-medium text-accent">
              {validDivs.length} divis{validDivs.length === 1 ? "ão" : "ões"} ·
              {" "}Total {totalBtu.toLocaleString("pt-PT")} BTU ·
              {" "}{topoLabel}{brand ? ` · ${brand}` : ""}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate(isEdit ? `/ac/${id}` : "/")}>Cancelar</Button>
        <Button onClick={submit} disabled={!canSubmit}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {isEdit ? "Guardar alterações" : "Criar orçamento"}
        </Button>
      </div>
    </div>
  )
}
